/**
 * FactorTypeSelectorMolecule.tsx
 *
 * A card-based selector for choosing factor type when creating a new factor.
 * Displays Manual, Related Spheres, and Arc Health options.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import type { StatusFactorType } from 'types/spherePoints';
import './FactorTypeSelectorMolecule.scss';

export interface FactorTypeOption {
  value: StatusFactorType;
  label: string;
  description: string;
  icon: string;
}

export interface FactorTypeSelectorMoleculeProps {
  /** Currently selected factor type */
  selectedType: StatusFactorType | null;
  /** Callback when a type is selected */
  onTypeSelect: (type: StatusFactorType) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional CSS class names */
  className?: string;
}

const FactorTypeSelectorMolecule: React.FC<FactorTypeSelectorMoleculeProps> = ({
  selectedType,
  onTypeSelect,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();

  // Factor type options
  const factorTypeOptions: FactorTypeOption[] = useMemo(
    () => [
      {
        value: 'manual',
        label: t('statusFactors.types.manual.label', 'Manual Check-In'),
        description: t(
          'statusFactors.types.manual.description',
          'Periodic manual status updates from responsible users'
        ),
        icon: 'pi pi-pencil',
      },
      {
        value: 'related_spheres',
        label: t('statusFactors.types.relatedSpheres.label', 'Related Spheres'),
        description: t(
          'statusFactors.types.relatedSpheres.description',
          'Aggregate status from child or related spheres'
        ),
        icon: 'pi pi-sitemap',
      },
      {
        value: 'arc_health',
        label: t('statusFactors.types.arcHealth.label'),
        description: t('statusFactors.types.arcHealth.description'),
        icon: 'pi pi-directions',
      },
    ],
    [t]
  );

  const handleTypeClick = (type: StatusFactorType) => {
    if (!disabled) {
      onTypeSelect(type);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, type: StatusFactorType) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTypeClick(type);
    }
  };

  const classes = [
    'factor-type-selector',
    disabled && 'factor-type-selector--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <h5 className="factor-type-selector__title">
        {t('statusFactors.selectFactorType', 'Select Factor Type')}
      </h5>
      <div className="factor-type-selector__options">
        {factorTypeOptions.map((option) => (
          <div
            key={option.value}
            role="button"
            tabIndex={disabled ? -1 : 0}
            className={[
              'factor-type-card',
              selectedType === option.value && 'factor-type-card--selected',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handleTypeClick(option.value)}
            onKeyDown={(e) => handleKeyDown(e, option.value)}
            aria-pressed={selectedType === option.value}
            aria-disabled={disabled}
          >
            <div className="factor-type-card__icon">
              <i className={option.icon} />
            </div>
            <div className="factor-type-card__content">
              <span className="factor-type-card__label">{option.label}</span>
              <span className="factor-type-card__description">{option.description}</span>
            </div>
            {selectedType === option.value && (
              <div className="factor-type-card__check">
                <i className="pi pi-check" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

FactorTypeSelectorMolecule.displayName = 'FactorTypeSelectorMolecule';

export default FactorTypeSelectorMolecule;
